import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatTerminDate } from "@/shared/format/termin-date";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { ensureGsap, prefersReducedMotion } from "@/shared/lib/gsap";
import { stripHtml } from "@/shared/lib/html";
import { cn } from "@/shared/lib/utils";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  Handshake,
  MapPin,
  Phone,
  Star,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { LogoStrip } from "./logo-strip";
import { HCA_PHONE_DISPLAY, HCA_PHONE_TEL, usePublicActions } from "./public-actions";
import { TestimonialSlider } from "./testimonial-slider";

const HERO_IMAGES = [
  {
    src: "/hca-home/hero-conference.jpg",
    webp: "/hca-home/hero-conference.webp",
    alt: "Konferenční sál HCA Moravia s účastníky business programu",
    position: "center center",
  },
  {
    src: "/hca-home/audience.jpg",
    webp: "/hca-home/audience.webp",
    alt: "Majitelé a manažeři firem během programu HCA Moravia",
    position: "center center",
  },
  {
    src: "/hca-home/networking.jpg",
    webp: "/hca-home/networking.webp",
    alt: "Networking účastníků HCA Moravia po semináři",
    position: "center center",
  },
] as const;

const VALUE_CARDS = [
  {
    src: "/hca-home/networking.jpg",
    webp: "/hca-home/networking.webp",
    alt: "Networking po akci HCA Moravia",
    word: "Setkání",
    caption: "Komunita majitelů a ředitelů",
  },
  {
    src: "/hca-home/audience.jpg",
    webp: "/hca-home/audience.webp",
    alt: "Živá účast na semináři HCA Moravia",
    word: "Růst",
    caption: "Praktické semináře a nácvik",
  },
  {
    src: "/hca-home/community.jpg",
    webp: "/hca-home/community.webp",
    alt: "Komunita absolventů HCA Moravia",
    word: "Spojení",
    caption: "Dlouhodobé propojení s lidmi z praxe",
  },
] as const;

/** Statický fallback Google hodnocení — web nikdy nesmí ukázat prázdno. */
const GOOGLE_RATING_FALLBACK = { rating: 4.9, count: 53 } as const;

/** Rating česky s čárkou a jedním desetinným místem — 4.9 → „4,9". */
function formatGoogleRating(rating: number): string {
  return rating.toLocaleString("cs-CZ", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Správné skloňování počtu recenzí: 1 recenze / 2–4 recenze / 5+ recenzí. */
function formatRecenzeCount(count: number): string {
  if (count === 1) return "1 recenze";
  if (count >= 2 && count <= 4) return `${count} recenze`;
  return `${count} recenzí`;
}

/** Veřejná úvodní stránka s hero, nadcházejícími termíny a benefity. */
export function HomePage() {
  const { openDiagnoza, openKontakt } = usePublicActions();
  const terminyQuery = trpc.hca.terminy.listPublic.useQuery({});
  const upcoming = terminyQuery.data?.slice(0, 3) ?? [];
  const [activeHero, setActiveHero] = useState(0);

  // Živé Google hodnocení — server drží 24h cache, dotaz je proto levný.
  // Žádný spinner: dokud data nepřijdou (nebo endpoint vrátí null / selže),
  // drží se statický fallback.
  const googleRatingQuery = trpc.hca.products.googleRating.useQuery(undefined, {
    staleTime: 60 * 60_000,
    refetchOnWindowFocus: false,
  });
  const googleRating = googleRatingQuery.data ?? GOOGLE_RATING_FALLBACK;

  const heroRef = useRef<HTMLElement>(null);
  const valueCardsRef = useGsapReveal<HTMLElement>({
    childrenSelector: "article",
    stagger: 0.15,
  });
  const benefitsRef = useGsapReveal<HTMLElement>({ y: 40 });
  const terminyRef = useGsapReveal<HTMLElement>({ y: 30 });
  const ctaRef = useGsapReveal<HTMLElement>({ y: 30 });

  useEffect(() => {
    const gsap = ensureGsap();
    const root = heroRef.current;
    if (!gsap || !root) return;

    const targets = root.querySelectorAll<HTMLElement>("[data-hero-stagger]");

    if (prefersReducedMotion()) {
      gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      gsap.set(targets, { opacity: 0, y: 24 });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: 0.85,
        stagger: 0.12,
        delay: 0.15,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMotion.matches || HERO_IMAGES.length < 2) return;

    const interval = window.setInterval(() => {
      setActiveHero((current) => (current + 1) % HERO_IMAGES.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col" data-testid="hca-home-page">
      <section
        ref={heroRef}
        className="relative isolate flex min-h-[660px] items-end overflow-hidden bg-[color:var(--hca-navy)] px-4 pb-32 pt-28 text-[color:var(--hca-ivory)] sm:min-h-[720px] sm:pb-36 lg:min-h-[780px]"
        data-testid="hca-home-hero"
      >
        {HERO_IMAGES.map((image, index) => (
          <picture
            key={image.src}
            className={cn(
              "absolute inset-0 -z-30 transition-opacity duration-1000",
              index === activeHero ? "opacity-100" : "opacity-0",
            )}
          >
            <source srcSet={image.webp} type="image/webp" />
            <img
              src={image.src}
              alt={image.alt}
              className={cn(
                "h-full w-full object-cover transition-transform duration-[6500ms]",
                index === activeHero && "scale-[1.05]",
              )}
              style={{ objectPosition: image.position }}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
          </picture>
        ))}
        <div
          className="absolute inset-0 -z-20"
          style={{
            background:
              "linear-gradient(90deg, rgba(18, 59, 109, 0.98) 0%, rgba(18, 59, 109, 0.92) 42%, rgba(18, 59, 109, 0.58) 72%, rgba(18, 59, 109, 0.28) 100%)",
          }}
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(180deg, rgba(18, 59, 109, 0.2) 0%, rgba(18, 59, 109, 0) 45%, rgba(18, 59, 109, 0.92) 100%)",
          }}
        />

        <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-3xl">
            <div data-hero-stagger className="mb-6 flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-12 bg-[color:var(--hca-gold)] md:w-16" />
              <span className="font-bold text-[color:var(--hca-gold)] text-base uppercase tracking-[0.28em] md:text-lg">
                Porosteme společně
              </span>
            </div>
            <h1
              data-hero-stagger
              className="mb-5 max-w-4xl text-4xl font-bold leading-[1.04] sm:text-5xl md:text-6xl"
              data-testid="hca-home-hero-title"
            >
              Praktické vzdělávání pro majitele, lídry a obchodní týmy
            </h1>
            <p
              data-hero-stagger
              className="mb-8 max-w-2xl text-lg leading-8 text-[color:var(--hca-ivory)]/90 sm:text-xl"
            >
              Praktické semináře a diagnostika pro ty, kdo rozhodují. Začněte rychlým zhodnocením
              firmy nebo vyberte nejbližší termín programu.
            </p>
            <div data-hero-stagger className="flex flex-col gap-3 sm:flex-row">
              <Button
                type="button"
                size="lg"
                className="h-12 w-full px-5 sm:w-auto"
                onClick={openDiagnoza}
                data-testid="hca-home-hero-diagnoza-button"
              >
                <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
                Spustit diagnostiku
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 w-full border-white/40 bg-white/10 px-5 text-white backdrop-blur hover:bg-white/18 hover:text-white sm:w-auto"
                asChild
              >
                <Link to="/terminy" data-testid="hca-home-hero-terminy-link">
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  Přihlásit se na seminář
                </Link>
              </Button>
            </div>
            <a
              data-hero-stagger
              href={`tel:${HCA_PHONE_TEL}`}
              className="mt-5 mb-10 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--hca-ivory)]/90 hover:text-white sm:mb-14"
              data-testid="hca-home-hero-phone"
            >
              <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
              Domluvit další krok: {HCA_PHONE_DISPLAY}
            </a>
          </div>

          <a
            data-hero-stagger
            href="https://www.google.com/search?q=HCA+Czechia+Brno"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden flex-col gap-2 rounded-xl bg-white/8 px-5 py-4 text-left text-[color:var(--hca-ivory)] ring-1 ring-white/15 backdrop-blur-md transition-colors hover:bg-white/14 hover:ring-white/25 lg:flex lg:mb-10"
            data-testid="hca-home-hero-google-rating"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[color:var(--hca-ivory)]/70">
              Google hodnocení
            </span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold tabular-nums leading-none">
                {formatGoogleRating(googleRating.rating)}
              </span>
              <div className="flex" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={`hero-star-${i}`} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm text-[color:var(--hca-ivory)]/80">
                ({formatRecenzeCount(googleRating.count)})
              </span>
            </div>
            <div className="border-t border-white/15 pt-2 text-sm">
              <div className="font-semibold">HCA Moravia</div>
              <div className="text-[color:var(--hca-ivory)]/75">Manažerské poradenství v Brně</div>
            </div>
          </a>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-0 border-t border-white/15 bg-[color:var(--hca-navy)]/55 backdrop-blur-sm">
          <LogoStrip variant="hero" />
        </div>
      </section>

      <section
        ref={valueCardsRef}
        className="bg-[color:var(--hca-navy)] px-4 py-16 text-[color:var(--hca-ivory)]"
        data-testid="hca-home-value-cards"
      >
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          {VALUE_CARDS.map((item) => (
            <article
              key={item.word}
              className="group relative aspect-[4/5] overflow-hidden rounded-lg border border-white/10 bg-white/[0.04]"
            >
              <picture>
                <source srcSet={item.webp} type="image/webp" />
                <img
                  src={item.src}
                  alt={item.alt}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  loading="lazy"
                />
              </picture>
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-[color:var(--hca-navy)]/85 via-[color:var(--hca-navy)]/30 to-transparent"
              />
              <h2 className="pointer-events-none absolute bottom-4 left-5 whitespace-nowrap font-extrabold text-5xl text-[color:var(--hca-ivory)] uppercase leading-none tracking-tight transition-transform duration-500 ease-out group-hover:-translate-y-9 md:text-6xl">
                {item.word}
              </h2>
              <span className="pointer-events-none absolute right-4 bottom-5 left-4 text-[color:var(--hca-ivory)] text-sm font-medium tracking-wide opacity-0 transition-opacity duration-500 delay-150 ease-out group-hover:opacity-100">
                {item.caption}
              </span>
            </article>
          ))}
        </div>
      </section>

      <section
        ref={benefitsRef}
        className="px-4 py-16"
        data-testid="hca-home-benefits"
        aria-label="Přínosy programů"
      >
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-10 lg:grid-cols-[5fr_7fr] lg:gap-14">
            {/* Foto Ondry — full body portrét, klidný výraz, gesty ruka. */}
            <div className="mx-auto w-full max-w-md lg:max-w-none">
              <img
                src="/hca-team/ondra-portrait.jpg"
                alt="Ondřej Křelina, CEO HCA Moravia"
                className="aspect-[3/4] w-full rounded-lg object-cover shadow-xl"
                loading="lazy"
              />
            </div>

            {/* Osobní dopis. */}
            <div>
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
                <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
                  Jsem v tom s vámi
                </div>
              </div>
              <h2 className="mt-4 max-w-xl font-bold text-4xl leading-[1.1] tracking-tight sm:text-5xl">
                Vím, jaké to je rozhodovat sám.
              </h2>
              <div className="mt-6 space-y-4 text-base text-muted-foreground leading-relaxed sm:text-lg">
                <p>
                  Vedení firmy je často osamělé poslání. Doma se vás nezeptají, co jste dnes
                  rozhodli a proč. V práci nemůžete řešit pochybnosti s týmem — měli byste mít
                  jistotu.
                </p>
                <p>
                  13 let pomáhám majitelům a vedoucím firem najít nadhled, který v každodenním rytmu
                  chybí. Ne formou jednoho semináře, ale dlouhodobě — diagnostikou, konzultacemi,
                  ročními programy. Pojďme si promluvit o tom, co teď řešíte vy.
                </p>
              </div>
              <div className="mt-6 flex items-center gap-3 border-[color:var(--hca-gold)]/40 border-t pt-5">
                <div className="h-px w-10 bg-[color:var(--hca-gold)]" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-foreground">Ondřej Křelina</div>
                  <div className="text-muted-foreground text-sm">CEO a majitel HCA Moravia</div>
                </div>
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  size="lg"
                  onClick={() =>
                    openKontakt({
                      initialMessage: "Mám zájem o nezávazný rozhovor s Ondřejem Křelinou.\n\n",
                    })
                  }
                  data-testid="hca-home-benefits-talk-button"
                >
                  <Handshake className="h-4 w-4" aria-hidden="true" />
                  Domluvte si rozhovor
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={openDiagnoza}
                  data-testid="hca-home-benefits-cta"
                >
                  <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
                  Začněte diagnostikou
                </Button>
              </div>
            </div>
          </div>

          {/* Reference klientů — důkaz pod osobním dopisem. */}
          <div className="mt-16">
            <TestimonialSlider />
          </div>
        </div>
      </section>

      <section
        ref={terminyRef}
        className="bg-muted/30 px-4 py-16"
        data-testid="hca-home-upcoming"
        aria-label="Nadcházející termíny"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-sm font-semibold uppercase tracking-wider text-primary">
                Nadcházející termíny
              </div>
              <h2 className="mt-2 text-3xl font-bold tracking-tight">Otevřené semináře</h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                Vyberte konkrétní termín. Přihláška se přiřadí přímo ke zvolenému semináři.
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/terminy" data-testid="hca-home-upcoming-all-link">
                Zobrazit všechny termíny
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          {terminyQuery.isPending ? (
            <div className="space-y-3" data-testid="hca-home-upcoming-skeleton">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          ) : upcoming.length === 0 ? (
            <div
              className="rounded-lg border border-border bg-background p-8 text-center"
              data-testid="hca-home-upcoming-empty"
            >
              <p className="text-muted-foreground">
                Momentálně není otevřený žádný termín. Spusťte diagnostiku a doporučíme vhodný další
                krok.
              </p>
              <Button
                type="button"
                className="mt-4"
                onClick={openDiagnoza}
                data-testid="hca-home-upcoming-empty-diagnoza-button"
              >
                Spustit diagnostiku
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="hca-home-upcoming-list">
              {upcoming.map((termin) => (
                <TerminRow key={termin.id} termin={termin} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section
        ref={ctaRef}
        className="relative px-4 py-20"
        style={{
          background: "linear-gradient(180deg, rgba(18,59,109,0.04) 0%, rgba(18,59,109,0.1) 100%)",
        }}
        data-testid="hca-home-cta"
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[color:var(--hca-gold)]/70 to-transparent"
        />
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
              <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
                Další krok
              </div>
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">
              Zjistěte, co má pro vaši firmu největší prioritu
            </h2>
            <p className="mt-3 text-muted-foreground">
              Během pár minut získáte orientační doporučení. Pro vedení firmy je to rychlý způsob,
              jak se rozhodnout, kterým programem začít.
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <Button
              type="button"
              size="lg"
              className="h-12"
              onClick={openDiagnoza}
              data-testid="hca-home-cta-diagnoza-button"
            >
              Spustit diagnostiku
            </Button>
            <Button size="lg" variant="outline" className="h-12" asChild>
              <a href={`tel:${HCA_PHONE_TEL}`} data-testid="hca-home-cta-phone">
                <Phone className="h-4 w-4" aria-hidden="true" />
                {HCA_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

interface TerminRowProps {
  termin: {
    id: string;
    datumOd: string;
    datumDo: string;
    misto: string;
    kapacitaMax: number;
    kapacitaObsazena: number;
    product?: {
      nazev?: string | null;
      slug?: string | null;
      kategorie?: string | null;
      popis?: string | null;
    } | null;
  };
}

function TerminRow({ termin }: TerminRowProps) {
  const volno = Math.max(0, termin.kapacitaMax - termin.kapacitaObsazena);
  const nazev = termin.product?.nazev ?? "Seminář HCA";
  const kategorie = termin.product?.kategorie ?? "Seminář";

  return (
    <article
      className="rounded-lg border border-border bg-background p-5 transition hover:border-primary/40 hover:shadow-sm"
      data-testid={`hca-home-termin-row-${termin.id}`}
    >
      <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            {kategorie}
          </div>
          <h3 className="mt-1 text-xl font-semibold text-foreground">{nazev}</h3>
          <p className="mt-2 line-clamp-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            {stripHtml(termin.product?.popis ?? "") ||
              "Praktický seminář pro lidi, kteří rozhodují o týmu, obchodu a výsledku firmy."}
          </p>
          {termin.product?.slug ? (
            <Link
              to="/seminare/$slug"
              params={{ slug: termin.product.slug }}
              className="mt-1 inline-block text-primary text-sm font-medium hover:underline"
              data-testid={`hca-home-termin-more-${termin.id}`}
            >
              Více o semináři →
            </Link>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" aria-hidden="true" />
              {formatTerminDate(termin.datumOd, termin.datumDo)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              {termin.misto}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Volná místa:{" "}
              <strong className="text-foreground">
                {volno} z {termin.kapacitaMax}
              </strong>
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row md:flex-col">
          <Button asChild data-testid={`hca-home-termin-register-${termin.id}`}>
            <Link to="/registrace/$terminId" params={{ terminId: termin.id }}>
              Přihlásit se
            </Link>
          </Button>
          {termin.product?.slug ? (
            <Button variant="outline" asChild data-testid={`hca-home-termin-detail-${termin.id}`}>
              <Link to="/seminare/$slug" params={{ slug: termin.product.slug }}>
                Detail
              </Link>
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
